import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { MapPin, Compass, RefreshCcw, Loader2 } from "lucide-react";
import TaskModal from "./TaskModal";
import { getWorkspace } from "../lib/workspaces";
import { toast } from "sonner";
import { cn } from "../lib/utils";

const STORAGE_KEY = "flowtasks_geo_allowed";

function formatDistance(m) {
  if (m == null) return "";
  if (m < 1000) return `${m}m away`;
  return `${(m / 1000).toFixed(1)}km away`;
}

/**
 * Nearby tasks widget.
 * - Asks for geolocation permission once (state persisted in localStorage)
 * - Polls /api/tasks/nearby?lat=...&lng=... every 60s while mounted + permission granted
 * - Click a task → opens the edit modal
 */
export default function NearbyTasks() {
  const [allowed, setAllowed] = useState(() => localStorage.getItem(STORAGE_KEY) === "1");
  const [coords, setCoords] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError("Your browser doesn't support location.");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setAllowed(true);
        localStorage.setItem(STORAGE_KEY, "1");
        setError("");
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        if (err.code === err.PERMISSION_DENIED) {
          setAllowed(false);
          localStorage.removeItem(STORAGE_KEY);
          setError("Location access denied. Update your browser permissions to use this feature.");
        } else {
          setError(err.message || "Could not get your location.");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  }, []);

  const fetchNearby = useCallback(
    async (c) => {
      if (!c) return;
      setLoading(true);
      try {
        const { data } = await api.get("/tasks/nearby", { params: { lat: c.lat, lng: c.lng } });
        // Notify on tasks newly entered our radius
        setTasks((prev) => {
          const prevIds = new Set(prev.map((t) => t.id));
          for (const t of data || []) {
            if (!prevIds.has(t.id)) {
              toast(
                `📍 You're near a task: ${t.title}`,
                {
                  description: t.location?.name || t.location?.address || formatDistance(t._distance_meters),
                  duration: 6000,
                }
              );
            }
          }
          return data || [];
        });
        setError("");
      } catch (e) {
        setError(formatApiError(e?.response?.data?.detail) || "Could not load nearby tasks.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Auto-request location when allowed (re-open of page)
  useEffect(() => {
    if (allowed && !coords) {
      requestLocation();
    }
  }, [allowed, coords, requestLocation]);

  // Fetch + poll while we have coords
  useEffect(() => {
    if (!coords) return;
    fetchNearby(coords);
    const id = setInterval(() => fetchNearby(coords), 60000);
    return () => clearInterval(id);
  }, [coords, fetchNearby]);

  return (
    <section
      data-testid="nearby-tasks-widget"
      className="rounded-xl border border-border bg-card p-5 mb-5"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Compass size={16} className="text-primary" strokeWidth={2} />
          </div>
          <div>
            <h2 className="font-display font-bold text-sm uppercase tracking-wider">Nearby tasks</h2>
            <p className="text-[11px] text-muted-foreground">
              Tasks with a location that match where you are right now.
            </p>
          </div>
        </div>
        {coords && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => fetchNearby(coords)}
            disabled={loading}
            data-testid="nearby-refresh-btn"
            className="text-xs"
          >
            {loading ? <Loader2 size={12} className="animate-spin mr-1" /> : <RefreshCcw size={12} className="mr-1" />}
            Refresh
          </Button>
        )}
      </div>

      {!allowed && (
        <div data-testid="nearby-permission-prompt" className="text-center py-6">
          <MapPin size={22} className="mx-auto text-muted-foreground mb-2" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground mb-3 max-w-sm mx-auto">
            Grant location access to see tasks tied to places you're near right now. Updates every minute.
          </p>
          <Button
            type="button"
            onClick={requestLocation}
            disabled={loading}
            data-testid="nearby-enable-btn"
          >
            {loading ? (
              <Loader2 size={14} className="mr-1.5 animate-spin" />
            ) : (
              <MapPin size={14} className="mr-1.5" />
            )}
            Enable location
          </Button>
          {error && <p className="text-xs text-destructive mt-3">{error}</p>}
        </div>
      )}

      {allowed && (
        <>
          {error && (
            <div className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2 mb-3">
              {error}
            </div>
          )}
          {tasks.length === 0 && !loading ? (
            <div
              data-testid="nearby-empty"
              className="rounded-md border border-dashed border-border bg-secondary/20 py-6 text-center text-xs text-muted-foreground"
            >
              {coords
                ? "No tasks near your current location."
                : "Getting your location…"}
            </div>
          ) : (
            <ul className="space-y-2">
              {tasks.map((t) => {
                const ws = getWorkspace(t.dashboard);
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => setEditing(t)}
                      data-testid={`nearby-task-${t.id}`}
                      className={cn(
                        "w-full text-left flex items-center gap-3 rounded-md border border-border bg-secondary/30 px-3 py-2.5",
                        "hover:bg-secondary/60 transition-colors"
                      )}
                      style={{ borderLeft: `3px solid ${ws.color}` }}
                    >
                      <div
                        className="w-9 h-9 rounded-md flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${ws.color}1A`, color: ws.color }}
                      >
                        <MapPin size={15} strokeWidth={2} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{t.title}</div>
                        <div className="text-[11px] text-muted-foreground truncate">
                          {t.location?.name || t.location?.address || "Saved place"} ·{" "}
                          <span style={{ color: ws.color }}>{ws.label}</span>
                        </div>
                      </div>
                      <span className="text-[11px] font-mono shrink-0" style={{ color: ws.color }}>
                        {formatDistance(t._distance_meters)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}

      <TaskModal open={!!editing} onClose={() => setEditing(null)} task={editing} />
    </section>
  );
}
